import { useState, useRef } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Avatar,
  Divider,
  Paper,
  IconButton
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import SendIcon from '@mui/icons-material/Send'
import DownloadIcon from '@mui/icons-material/Download'
import { useMessages, useAddMessage } from '@services/helpdesk/helpdesk.hooks'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import UploadProgress from '@components/UploadProgress'

interface HelpdeskMessagesProps {
  ticketId: number
}

// Function to check if URL points to an image
const isImageUrl = (url: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
  const lowerCaseUrl = url.toLowerCase()
  return imageExtensions.some((ext) => lowerCaseUrl.endsWith(ext))
}

const HelpdeskMessages = ({ ticketId }: HelpdeskMessagesProps) => {
  const { data: messagesData, isLoading, error } = useMessages(ticketId)
  const { mutate: addMessage, isPending: isAddingMessage, error: addError } = useAddMessage()

  // Get messages array from paginated response
  const messages = messagesData?.results || []

  const [newMessage, setNewMessage] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(event.target.value)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0]
      setAttachment(file)
    }
  }

  const handleRemoveAttachment = () => {
    setAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault()
    }

    if (newMessage.trim()) {
      addMessage(
        {
          ticketId,
          message: newMessage,
          attachment: attachment || undefined
        },
        {
          onSuccess: () => {
            setNewMessage('')
            setAttachment(null)
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
          },
          onError: (error) => {
            console.error('Error adding message:', error)
            alert(`Błąd podczas dodawania wiadomości: ${(error as Error).message}`)
          }
        }
      )
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ my: 2 }}>
        Wystąpił błąd podczas ładowania wiadomości: {(error as Error).message}
      </Alert>
    )
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Wiadomości
      </Typography>

      {messages.length === 0 ? (
        <Alert severity='info' sx={{ my: 2 }}>
          Brak wiadomości dla tego zgłoszenia.
        </Alert>
      ) : (
        <Box sx={{ mb: 4 }}>
          {messages.map((message) => (
            <Card key={message.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {message.author
                      ? `${message.author.firstName.charAt(0)}${message.author.lastName.charAt(0)}`
                      : 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant='subtitle1'>
                      {message.author
                        ? `${message.author.firstName} ${message.author.lastName}`
                        : 'Użytkownik nieznany'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {format(new Date(message.createdAt), 'dd.MM.yyyy HH:mm', { locale: pl })}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant='body1' sx={{ mb: 2 }}>
                  {message.message}
                </Typography>

                {message.attachment && (
                  <Box sx={{ mt: 2 }}>
                    {isImageUrl(message.attachment) ? (
                      // If attachment is an image, display it
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <img
                          src={message.attachment}
                          alt='Załącznik'
                          style={{
                            maxWidth: '100%',
                            maxHeight: '300px',
                            borderRadius: '4px',
                            display: 'block',
                            marginBottom: '8px'
                          }}
                        />
                        <Button
                          startIcon={<DownloadIcon />}
                          variant='outlined'
                          size='small'
                          href={message.attachment}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          Pobierz oryginał
                        </Button>
                      </Box>
                    ) : (
                      // If it's another file type, only show download button
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                          startIcon={<DownloadIcon />}
                          variant='outlined'
                          size='small'
                          href={message.attachment}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          Pobierz załącznik
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant='h6' gutterBottom>
        Dodaj wiadomość
      </Typography>

      <Paper elevation={0} variant='outlined' sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder='Wpisz wiadomość...'
            value={newMessage}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            sx={{ mb: 2 }}
            disabled={isAddingMessage}
          />

          {attachment && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2'>
                Załącznik: {attachment.name} ({(attachment.size / 1024).toFixed(1)} KB)
              </Typography>
              <IconButton size='small' onClick={handleRemoveAttachment} disabled={isAddingMessage}>
                ×
              </IconButton>
            </Box>
          )}

          {addError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              Błąd podczas dodawania wiadomości: {(addError as Error).message}
            </Alert>
          )}

          <UploadProgress />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              type='button'
              startIcon={<AttachFileIcon />}
              onClick={handleAttachClick}
              disabled={isAddingMessage || !!attachment}
            >
              Dodaj załącznik
            </Button>
            <Button
              type='submit'
              variant='contained'
              endIcon={<SendIcon />}
              disabled={isAddingMessage || !newMessage.trim()}
            >
              {isAddingMessage ? <CircularProgress size={24} /> : 'Wyślij'}
            </Button>
          </Box>

          <input
            type='file'
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt'
          />
        </form>
      </Paper>
    </Box>
  )
}

export default HelpdeskMessages
